// Auth store — sessão do usuário (magic link via Supabase)

import { supabase } from './supabase';
import { startSync, stopSync } from './sync';

export interface AuthUser {
  id: string;
  email: string;
}

export interface AuthSnapshot {
  user: AuthUser | null;
  /** true depois que a sessão inicial foi resolvida (evita flicker) */
  ready: boolean;
}

let snapshot: AuthSnapshot = { user: null, ready: false };
const listeners = new Set<() => void>();

function notify() {
  listeners.forEach((l) => l());
}

export function getAuthSnapshot(): AuthSnapshot {
  return snapshot;
}

export function subscribeAuth(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

let initialized = false;

export function initAuth(): void {
  if (initialized) return;
  initialized = true;

  supabase.auth.onAuthStateChange((_event, session) => {
    const user = session?.user ? { id: session.user.id, email: session.user.email ?? '' } : null;
    snapshot = { user, ready: true };
    notify();
    if (user) {
      startSync(user.id);
    } else {
      stopSync();
    }
  });
}

/** traduz os erros comuns do Supabase pra pt-BR */
function friendlyError(msg: string): string {
  const m = msg.toLowerCase();
  if (m.includes('invalid login credentials')) return 'Email ou senha incorretos';
  if (m.includes('already registered')) return 'Este email já tem conta — use ENTRAR';
  if (m.includes('at least 6 characters')) return 'A senha precisa de pelo menos 6 caracteres';
  if (m.includes('rate limit') || m.includes('security purposes'))
    return 'Limite de emails atingido — espera ~1h e tenta de novo';
  if (m.includes('email not confirmed'))
    return 'Email ainda não confirmado — clica no link que enviamos, ou peça novo link mágico';
  if (m.includes('fetch')) return 'Sem conexão — verifica sua internet';
  return msg;
}

export async function signUpWithPassword(
  email: string,
  password: string,
): Promise<{ error: string | null; needsConfirmation: boolean }> {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: { emailRedirectTo: window.location.origin },
  });
  if (error) return { error: friendlyError(error.message), needsConfirmation: false };
  // se "Confirm email" estiver ligado no projeto, session vem null
  return { error: null, needsConfirmation: !data.session };
}

export async function signInWithPassword(
  email: string,
  password: string,
): Promise<{ error: string | null }> {
  const { error } = await supabase.auth.signInWithPassword({ email, password });
  return { error: error ? friendlyError(error.message) : null };
}

/** magic link — usado como "esqueci a senha" e fallback sem senha */
export async function signInWithMagicLink(email: string): Promise<{ error: string | null }> {
  const { error } = await supabase.auth.signInWithOtp({
    email,
    options: { emailRedirectTo: window.location.origin },
  });
  return { error: error ? friendlyError(error.message) : null };
}

/** define/troca a senha do usuário logado (fluxo pós-magic-link) */
export async function updatePassword(newPassword: string): Promise<{ error: string | null }> {
  const { error } = await supabase.auth.updateUser({ password: newPassword });
  return { error: error ? friendlyError(error.message) : null };
}

export async function signOut(): Promise<void> {
  await supabase.auth.signOut();
}
